import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { PerfilInvestidor } from '../../../core/interface/perfil-investidor';

interface SegmentoBarra {
  titulo: string;
  largura: number;
  cor: string;
  isPrimeiro: boolean;
  isUltimo: boolean;
}

@Component({
  selector: 'app-modal-explicativo',
  imports: [CommonModule],
  templateUrl: './modal-explicativo.component.html',
  styleUrl: './modal-explicativo.component.scss',
})
export class ModalExplicativoComponent {
  @Input() visible = false;
  @Input() score = 0; 
  @Input() title = '';
  @Input() perfil: PerfilInvestidor[] = [];

  @Output() close = new EventEmitter<void>();

  // Paleta de cores para os blocos (adicionado uma cor para o Dinâmico)
  private coresPerfis = ['#D9D9D9', '#3C2E88', '#F0C93A', '#E33E5A', '#3B82F6']; // Roxo, Laranja, Vermelho, Verde, Azul

  /**
   * Ordena os perfis pela pontuação mínima.
   */
  private get perfisOrdenados(): PerfilInvestidor[] {
    return [...this.perfil].sort(
      (a, b) => a.pontuacaoMinima - b.pontuacaoMinima
    );
  }

  /**
   * Menor pontuação encontrada entre todos os perfis.
   */
  private get pontuacaoMinimaGlobal(): number {
    if (!this.perfil?.length) {
      return 0;
    }

    return Math.min(
      ...this.perfil.map(p => p.pontuacaoMinima)
    );
  }

  /**
   * Maior pontuação encontrada entre todos os perfis.
   */
  private get pontuacaoMaximaGlobal(): number {
    if (!this.perfil?.length) {
      return 0;
    }

    return Math.max(
      ...this.perfil.map(p => p.pontuacaoMaxima)
    );
  }

  /**
   * Monta os segmentos da régua proporcionalmente
   * às faixas retornadas pela API.
   */
  get segmentos(): SegmentoBarra[] {
    if (!this.perfil?.length) {
      return [];
    }

    const perfisOrdenados = this.perfisOrdenados;

    const pontuacaoMinima = this.pontuacaoMinimaGlobal;
    const pontuacaoMaxima = this.pontuacaoMaximaGlobal;

    const intervaloTotal =
      pontuacaoMaxima - pontuacaoMinima + 1;

    if (intervaloTotal <= 0) {
      return [];
    }

    return perfisOrdenados.map((perfil, index) => {
      const tamanhoFaixa =
        perfil.pontuacaoMaxima -
        perfil.pontuacaoMinima +
        1;

      const larguraPorcentagem =
        (tamanhoFaixa / intervaloTotal) * 100;

      return {
        titulo: perfil.nomePerfilInvestimento,
        largura: larguraPorcentagem,
        cor: this.coresPerfis[index] || '#6B7280',
        isPrimeiro: index === 0,
        isUltimo: index === perfisOrdenados.length - 1
      };
    });
  }

  /**
   * Calcula a posição do marcador com base
   * na escala real retornada pela API.
   */
  get scorePercentage(): number {
    if (!this.perfil?.length) {
      return 0;
    }

    const pontuacaoMinima = this.pontuacaoMinimaGlobal;
    const pontuacaoMaxima = this.pontuacaoMaximaGlobal;

    const intervaloTotal =
      pontuacaoMaxima - pontuacaoMinima;

    if (intervaloTotal <= 0) {
      return 0;
    }

    const percentual =
      ((this.score - pontuacaoMinima) / intervaloTotal) * 100;

    return Math.max(
      0,
      Math.min(100, percentual)
    );
  }

  handleClose(): void {
    this.close.emit();
  }
}